import { CheckCircle2, Circle, Truck, PackageCheck, Banknote, ShieldAlert } from "lucide-react";

/**
 * Visual status tracker for a marketplace order.
 * Stages: Paid → Shipped → Delivered → Payout (with Dispute alternate branch).
 */
export default function OrderStatusTimeline({ order, className = "" }) {
  const status = order?.status || "";
  const escrow = order?.escrow_status || "";
  const disputed = escrow === "disputed" || !!order?.dispute_reason;

  const stages = [
    { key: "paid", label: "Buyer paid", icon: CheckCircle2, done: !!order?.created_at, ts: order?.created_at, testId: "stage-paid" },
    { key: "shipped", label: "Shipped", icon: Truck, done: ["shipped", "delivered", "completed"].includes(status) || !!order?.shipped_at, ts: order?.shipped_at, testId: "stage-shipped" },
    { key: "delivered", label: "Delivered", icon: PackageCheck, done: ["delivered", "completed"].includes(status) || !!order?.delivery_confirmed_at, ts: order?.delivery_confirmed_at, testId: "stage-delivered" },
    { key: "payout", label: "Paid out", icon: Banknote, done: escrow === "released" || !!order?.funds_released_at, ts: order?.funds_released_at, testId: "stage-payout" },
  ];

  const doneCount = stages.filter((s) => s.done).length;
  const progressPct = disputed ? 0 : Math.max(0, ((doneCount - 1) / (stages.length - 1)) * 100);

  return (
    <div className={`space-y-2 ${className}`} data-testid="order-status-timeline">
      <div className="relative">
        <div className="absolute left-0 right-0 top-3 h-0.5 bg-border" />
        <div
          className="absolute left-0 top-3 h-0.5 bg-primary transition-all"
          style={{ width: `${progressPct}%` }}
        />
        <div className="relative flex items-start justify-between">
          {stages.map((s) => {
            const Icon = s.icon;
            const active = s.done;
            return (
              <div key={s.key} className="flex w-1/4 flex-col items-center text-center" data-testid={s.testId}>
                <div
                  className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-muted-foreground"
                  }`}
                >
                  {active ? <Icon className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3" />}
                </div>
                <p className={`mt-1.5 text-[10px] font-medium uppercase tracking-wide ${active ? "text-foreground" : "text-muted-foreground"}`}>
                  {s.label}
                </p>
                {s.ts && (
                  <p className="text-[10px] text-muted-foreground">
                    {new Date(s.ts).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {disputed && (
        <div className="flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 p-1.5 text-[11px] text-amber-400" data-testid="stage-disputed">
          <ShieldAlert className="h-3.5 w-3.5" /> Under SRC review — payout paused until resolved.
        </div>
      )}
    </div>
  );
}
